import { Component, ChangeDetectionStrategy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';

import {
  TuiAlertService,
  TuiButtonModule,
  TuiLoaderModule,
  TuiErrorModule,
  TuiDataListModule,
} from '@taiga-ui/core';
import { TuiTextfieldControllerModule, TuiSvgModule } from '@taiga-ui/core';
import {
  TuiInputModule,
  TuiFieldErrorPipeModule,
  TuiSelectModule,
  TuiInputDateModule,
  TuiDataListWrapperModule,
  TuiInputCountModule,
} from '@taiga-ui/kit';
import {
  TuiDay,
  TuiStringHandler,
  TuiIdentityMatcher,
  TUI_DATE_SEPARATOR,
} from '@taiga-ui/cdk';

import { SalesService } from '../../../core/services/sales.service';
import { InventoryService } from '../../../core/services/inventory.service';

interface InventoryItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

@Component({
  selector: 'app-add-sales',
  providers: [{ provide: TUI_DATE_SEPARATOR, useValue: '/' }],
  templateUrl: './add-sales.component.html',
  styleUrls: ['./add-sales.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TuiButtonModule,
    TuiLoaderModule,
    TuiTextfieldControllerModule,
    TuiSvgModule,
    TuiInputModule,
    TuiErrorModule,
    TuiFieldErrorPipeModule,
    TuiSelectModule,
    TuiInputDateModule,
    TuiDataListWrapperModule,
    TuiInputCountModule,
    TuiDataListModule,
  ],
})
export class AddSalesComponent implements OnInit {
  form!: FormGroup;
  loading = false;
  currentDate!: TuiDay;
  paymentMethodOptions: readonly string[] = ['Online', 'Cash'];

  private recordId: string | null = null;
  isEditMode = false;

  inventoryItems: InventoryItem[] = [];
  availableItems: InventoryItem[] = [];
  inventoryLoading = true;

  readonly stringify: TuiStringHandler<InventoryItem> = (item) =>
    item ? `${item.name} — LKR ${(item.price ?? 0).toFixed(2)}` : '';

  readonly identityMatcher: TuiIdentityMatcher<InventoryItem> = (a, b) =>
    a?.id === b?.id;

  constructor(
    private fb: FormBuilder,
    private alertService: TuiAlertService,
    public router: Router,
    private salesService: SalesService,
    private inventoryService: InventoryService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      date: [this.getCurrentDate(), Validators.required],
      item: [null, Validators.required],
      quantity: [1, [Validators.required, Validators.min(1)]],
      price: [{ value: '', disabled: true }],
      paymentMethod: ['Cash', Validators.required],
    });

    this.currentDate = this.getCurrentDate();
    this.recordId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.recordId;

    this.loadInventoryItems();
    this.form.valueChanges.subscribe(() => this.updatePrice());
  }

private async loadInventoryItems(): Promise<void> {
  this.inventoryLoading = true;
  try {
    const res = await this.inventoryService.getAllInventory();
    const itemsArray: any[] = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];

    this.inventoryItems = itemsArray.map((it) => ({
      id: it._id || it.id,
      name: it.itemName,
      price: it.price || 0,
      quantity: it.quantity || 0,
    }));

    this.availableItems = this.inventoryItems.filter(i => i.quantity > 0);

    if (this.isEditMode && this.recordId) {
      await this.loadSaleData(this.recordId);
    }
  } catch {
    this.alertService.open('Failed to load inventory', { status: 'error' }).subscribe();
  } finally {
    this.inventoryLoading = false;  // 👈 stop loader
  }
}

  private async loadSaleData(id: string): Promise<void> {
    try {
      const sale = await this.salesService.getSale(id);
      if (!sale) return;

      const selectedItem =
        this.inventoryItems.find((i) => i.id === sale.items?.[0]?.itemId) ??
        null;

      this.form.patchValue({
        date: sale.date
          ? this.toTuiDay(new Date(sale.date))
          : this.getCurrentDate(),
        item: selectedItem,
        quantity: sale.items?.[0]?.quantity || 1,
        paymentMethod: sale.paymentMethod,
      });

      this.updatePrice();
    } catch {
      this.alertService
        .open('Failed to load sale', { status: 'error' })
        .subscribe();
    }
  }

  private updatePrice(): void {
    const item: InventoryItem | null = this.form.get('item')?.value;
    const qty = +this.form.get('quantity')?.value || 0;
    this.form
      .get('price')
      ?.setValue(item ? item.price * qty : 0, { emitEvent: false });
  }

  private getCurrentDate(): TuiDay {
    const d = new Date();
    return new TuiDay(d.getFullYear(), d.getMonth(), d.getDate());
  }

  toTuiDay(date: Date): TuiDay {
    return new TuiDay(date.getFullYear(), date.getMonth(), date.getDate());
  }

  cancel(): void {
    this.router.navigate(['/admin/sales']);
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;

    const raw = this.form.getRawValue();
    const payload = {
      ...raw,
      items: [
        {
          itemId: raw.item.id,
          itemName: raw.item.name,
          quantity: raw.quantity,
          price: raw.item.price * raw.quantity,
        },
      ],
    };

    try {
      await this.salesService.createSale(payload);

      const inv = this.inventoryItems.find((i) => i.id === raw.item.id);
      const newQuantity = (inv?.quantity || 0) - raw.quantity;
      await this.inventoryService.updateInventoryItem(raw.item.id, {
        quantity: newQuantity,
      });

      this.alertService.open(`Sale added!`, { status: 'success' }).subscribe();
      this.router.navigate(['/admin/sales']);
    } catch {
      this.alertService
        .open(`Failed to add sale`, { status: 'error' })
        .subscribe();
    } finally {
      this.loading = false;
    }
  }
}
